import { useState, useCallback, useRef } from "react";
import { uploadImage } from "@/lib/supabase";

interface ImageUploadFieldProps {
  value: string;
  onChange: (url: string) => void;
  folder?: string;
  label?: string;
  className?: string;
}

export default function ImageUploadField({
  value,
  onChange,
  folder = "general",
  label = "Image",
  className = "",
}: ImageUploadFieldProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const processFile = useCallback(
    async (file: File) => {
      if (!file.type.startsWith("image/")) {
        setError("Only image files are allowed (PNG, JPG, WEBP).");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError("Image must be under 5MB.");
        return;
      }
      setError(null);
      setIsUploading(true);
      const url = await uploadImage(file, folder);
      setIsUploading(false);
      if (url) {
        onChange(url);
      } else {
        setError("Upload failed. Please try again.");
      }
    },
    [folder, onChange]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) processFile(file);
    },
    [processFile]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) processFile(file);
    },
    [processFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const triggerFileInput = () => inputRef.current?.click();

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>

      {value ? (
        <div className="relative">
          <img
            src={value}
            alt="Uploaded preview"
            className="w-full h-48 object-cover rounded-lg border border-gray-200"
          />
          <button
            type="button"
            onClick={triggerFileInput}
            className="absolute bottom-2 right-2 px-3 py-1.5 bg-white/90 hover:bg-white text-xs font-medium text-gray-700 rounded-md shadow-sm border border-gray-200 transition-colors whitespace-nowrap"
          >
            Replace Image
          </button>
        </div>
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={triggerFileInput}
          className={`flex flex-col items-center justify-center gap-2 w-full h-40 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
            dragOver
              ? "border-gold-500 bg-gold-50"
              : "border-gray-300 hover:border-gray-400 bg-gray-50"
          }`}
        >
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100">
            <i className="ri-upload-cloud-2-line text-lg text-gray-500" />
          </div>
          <p className="text-sm text-gray-500">
            {isUploading ? "Uploading..." : "Drop image here or click to browse"}
          </p>
          <p className="text-xs text-gray-400">PNG, JPG, WEBP up to 5MB</p>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {isUploading && (
        <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
          <i className="ri-loader-4-line animate-spin" />
          Uploading image...
        </div>
      )}

      {error && (
        <div className="mt-2 flex items-center gap-1.5 text-sm text-red-600">
          <i className="ri-error-warning-line" />
          {error}
        </div>
      )}
    </div>
  );
}